import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, Clock, Copy, DoorOpen, LogOut, PhoneCall, PhoneIncoming, PhoneOff, Plus, RefreshCw, Search, Shield, UserCheck, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, PUBLIC_BASE_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";

// See the matching comment in SocietyMobileApp.jsx - the native app already
// rings and shows a call screen for incoming calls via the native FCM/Telecom
// flow, so this page's own poll-driven popup + ringtone should only run when
// there's no native flow at all (i.e. this page open in a plain browser).
const isNativeApp = typeof window !== "undefined" && Boolean(window.RpSocietyAndroid);
const WEB_CALL_DISABLED_MESSAGE = "Video calls work only in the RP Society Android app. Please use the native mobile app.";

const blankVisitor = {
  visitorName: "",
  visitorMobile: "",
  visitorPhoto: "",
  purpose: "Guest visit",
  wing: "A",
  flatNumber: "",
  vehicleNumber: "",
  visitorCount: 1,
  gateName: "Main Gate",
  securityName: "",
  remarks: "",
  contactTarget: "preferred",
};

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const normalized = String(status || "pending").toLowerCase();
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    completed: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${styles[normalized] || styles.pending}`}>{normalized}</span>;
}

function VisitorCard({ visitor, onCopy, onCall, onCheckout }) {
  const approvalUrl = `${PUBLIC_BASE_URL}/society/approve/${visitor.id}?token=${encodeURIComponent(visitor.approvalToken || "")}`;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-black text-slate-950">{visitor.visitorName || "Visitor"}</div>
          <div className="mt-1 text-sm text-slate-600">
            Flat <span className="font-bold text-slate-900">{visitor.wing} {visitor.flatNumber}</span>
            {visitor.ownerName ? ` · ${visitor.ownerName}` : " · Owner not found"}
          </div>
          <div className="mt-1 text-xs text-slate-500">{visitor.purpose || "Visit"} · {visitor.visitorMobile || "No mobile"}</div>
        </div>
        <StatusPill status={visitor.status} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" className="h-11" onClick={() => onCopy(approvalUrl)}>
          <Copy className="h-4 w-4" /> Link
        </Button>
        <Button type="button" variant="outline" className="h-11" onClick={() => onCall(visitor)}>
          <PhoneCall className="h-4 w-4" /> Call
        </Button>
        {visitor.status === "approved" && (
          <Button type="button" className="col-span-2 h-11 bg-slate-950 text-white hover:bg-slate-800" onClick={() => onCheckout(visitor)}>
            <DoorOpen className="h-4 w-4" /> Checkout
          </Button>
        )}
      </div>
    </div>
  );
}

function IncomingCallScreen({ call, onAnswer, onDecline }) {
  if (!call) return null;
  const caller = call.ownerName || "Resident";
  const flat = `${call.wing || ""} ${call.flatNumber || ""}`.trim();
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen flex-col justify-between bg-[#07111f] px-6 py-8 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#064e3b_0%,#07111f_45%,#020617_100%)]" />
      <div className="relative flex items-center justify-between">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.28em] text-white/55">RP Society Guard</div>
          <div className="mt-2 text-sm font-semibold text-white/70">Incoming resident call</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
          <Video className="h-5 w-5" />
        </div>
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-white/10 text-5xl font-black shadow-2xl ring-8 ring-white/5">
          {caller.slice(0, 1).toUpperCase()}
        </div>
        <div className="mt-8 text-4xl font-black leading-tight">{caller}</div>
        <div className="mt-3 text-lg font-semibold text-white/70">{flat ? `Flat ${flat}` : "Resident video call"}</div>
        <div className="mt-7 rounded-full bg-white/10 px-5 py-2 text-sm font-bold text-white/80">Ringing...</div>
      </div>
      <div className="relative mx-auto grid w-full max-w-sm grid-cols-2 gap-10 pb-4">
        <button type="button" onClick={onDecline} className="flex flex-col items-center gap-3 text-sm font-black text-white">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-rose-600 shadow-2xl shadow-rose-950/50">
            <PhoneOff className="h-8 w-8" />
          </span>
          Decline
        </button>
        <button type="button" onClick={onAnswer} className="flex flex-col items-center gap-3 text-sm font-black text-white">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 shadow-2xl shadow-emerald-950/50">
            <PhoneIncoming className="h-8 w-8" />
          </span>
          Answer
        </button>
      </div>
    </div>
  );
}

export default function SocietyGuardApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [owners, setOwners] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [form, setForm] = useState(blankVisitor);
  const [activeView, setActiveView] = useState("entry");
  const [search, setSearch] = useState("");
  const [ownerSearch, setOwnerSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const lastResidentCallNoticeRef = useRef("");
  // Only "ringing" calls should drive the incoming-call screen/ringtone.
  // activeCalls also includes "connected" calls (e.g. already answered on
  // this staff member's other device) - treating those as incoming too
  // meant a call that was already picked up elsewhere kept ringing here.
  const ringingCalls = useMemo(() => activeCalls.filter((call) => (call.status || "ringing") === "ringing"), [activeCalls]);
  const leadRingingCall = ringingCalls[0] || null;
  const activeCallId = leadRingingCall?.id || "";

  const load = useCallback(async () => {
    try {
      const [directoryRes, visitorsRes, callsRes] = await Promise.all([
        api.get("/society/directory"),
        api.get("/society/visitors"),
        api.get("/society/mobile/calls/active").catch(() => ({ data: [] })),
      ]);
      setOwners(Array.isArray(directoryRes.data) ? directoryRes.data : []);
      setVisitors(Array.isArray(visitorsRes.data) ? visitorsRes.data : []);
      setActiveCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load guard screen"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      load();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!user?.id || user?.role !== "society_staff") return undefined;
    let cancelled = false;
    const listeners = [];
    const setupPush = async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== "granted") return;
        listeners.push(await PushNotifications.addListener("registration", (token) => {
          if (!token?.value) return;
          api.post("/society/mobile/push-token", { token: token.value, platform: "android", enabled: true }).catch(() => {});
        }));
        listeners.push(await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
          const route = event?.notification?.data?.route;
          const callId = event?.notification?.data?.callId;
          if (route?.includes("/society/guard-call/") || route?.includes("/society/resident-call/") || callId) {
            toast.info(WEB_CALL_DISABLED_MESSAGE);
            navigate("/society/guard");
          } else if (route) navigate(route);
        }));
        try {
          const { LocalNotifications } = await import("@capacitor/local-notifications");
          listeners.push(await LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
            const route = event?.notification?.extra?.route;
            if (route?.includes("/society/guard-call/") || route?.includes("/society/resident-call/")) {
              toast.info(WEB_CALL_DISABLED_MESSAGE);
              navigate("/society/guard");
            } else if (route) navigate(route);
          }));
        } catch {}
        if (!cancelled) await PushNotifications.register();
      } catch {}
    };
    setupPush();
    return () => {
      cancelled = true;
      listeners.forEach((listener) => {
        try { listener.remove?.(); } catch {}
      });
    };
  }, [navigate, user?.id, user?.role]);

  useEffect(() => {
    if (isNativeApp) return;
    const leadCall = leadRingingCall;
    if (!leadCall?.id || lastResidentCallNoticeRef.current === leadCall.id) return;
    lastResidentCallNoticeRef.current = leadCall.id;
    toast.info(`${leadCall.ownerName || "Resident"} is calling`, { duration: 8000 });
    try { navigator.vibrate?.([450, 200, 450]); } catch {}
    // Let the resident's screen show "Ringing..." instead of just "Calling...".
    api.post(`/public/society/calls/${leadCall.id}/ring-ack`, null, { params: { role: "society_staff" } }).catch(() => {});
    import("@capacitor/local-notifications").then(async ({ LocalNotifications }) => {
      const permission = await LocalNotifications.requestPermissions();
      if (permission.display !== "granted") return;
      await LocalNotifications.schedule({
        notifications: [{
          id: Math.abs(Array.from(String(leadCall.id)).reduce((sum, char) => sum + char.charCodeAt(0), 0)) % 2147483647,
          title: "Incoming resident video call",
          body: `${leadCall.ownerName || "Resident"} from flat ${leadCall.wing || ""} ${leadCall.flatNumber || ""} is calling`,
          sound: "default",
          extra: { route: "/society/guard" },
        }],
      });
    }).catch(() => {});
  }, [leadRingingCall]);

  useEffect(() => {
    if (isNativeApp) return undefined;
    if (!activeCallId) {
      try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
      return undefined;
    }
    try { window.RpSocietyAndroid?.startCallRingtone?.(); } catch {}
    return () => {
      try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
    };
  }, [activeCallId]);

  useEffect(() => {
    if (user?.name) {
      setForm((current) => ({ ...current, securityName: current.securityName || user.name }));
    }
  }, [user?.name]);

  const wings = useMemo(() => {
    const values = Array.from(new Set(owners.map((owner) => String(owner.wing || "").trim()).filter(Boolean)));
    return values.length ? values.sort() : ["A", "B", "C"];
  }, [owners]);

  const selectedOwner = useMemo(() => {
    const wing = String(form.wing || "").trim().toUpperCase();
    const flat = String(form.flatNumber || "").trim().toUpperCase();
    if (!wing || !flat) return null;
    return owners.find((owner) => String(owner.wing || "").toUpperCase() === wing && String(owner.flatNumber || "").toUpperCase() === flat) || null;
  }, [owners, form.wing, form.flatNumber]);

  const ownerMatches = useMemo(() => {
    const query = ownerSearch.trim().toLowerCase();
    if (!query) return [];
    return owners.filter((owner) => {
      const haystack = `${owner.wing || ""} ${owner.flatNumber || ""} ${owner.ownerName || ""} ${owner.ownerMobile || ""} ${owner.ownerSecondaryMobile || ""} ${owner.intercomNumber || ""}`.toLowerCase();
      return haystack.includes(query);
    }).slice(0, 8);
  }, [owners, ownerSearch]);

  const selectOwner = (owner) => {
    setForm((current) => ({ ...current, wing: String(owner.wing || "A").toUpperCase(), flatNumber: String(owner.flatNumber || "").toUpperCase() }));
    setOwnerSearch(`${owner.wing || ""} ${owner.flatNumber || ""} - ${owner.ownerName || ""}`.trim());
  };

  const visibleVisitors = useMemo(() => {
    const query = search.trim().toLowerCase();
    return visitors.filter((visitor) => {
      if (activeView === "pending" && visitor.status !== "pending") return false;
      const haystack = `${visitor.visitorName || ""} ${visitor.visitorMobile || ""} ${visitor.ownerName || ""} ${visitor.wing || ""} ${visitor.flatNumber || ""} ${visitor.status || ""}`.toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [visitors, activeView, search]);

  const createVisitor = async (callAfterCreate = false) => {
    if (!form.visitorName.trim()) {
      toast.error("Visitor name is required");
      return;
    }
    if (!form.flatNumber.trim()) {
      toast.error("Flat number is required");
      return;
    }
    try {
      setBusy(true);
      const payload = { ...form, wing: form.wing.toUpperCase(), flatNumber: form.flatNumber.toUpperCase(), securityName: user?.name || form.securityName, visitorCount: Number(form.visitorCount) || 1 };
      const res = await api.post("/society/visitors", payload);
      const approvalUrl = `${PUBLIC_BASE_URL}/society/approve/${res.data.id}?token=${encodeURIComponent(res.data.approvalToken || "")}`;
      await navigator.clipboard?.writeText(approvalUrl);
      toast.success(callAfterCreate ? "Visitor entry created. Calling owner now." : "Visitor entry created. Approval link copied.");
      setForm({ ...blankVisitor, wing: form.wing, gateName: form.gateName, securityName: form.securityName });
      setActiveView("pending");
      await load();
      if (callAfterCreate) {
        await startCall(res.data);
      }
    } catch (err) {
      toast.error(formatErr(err, "Could not create visitor"));
    } finally {
      setBusy(false);
    }
  };

  const answerResidentCall = () => {
    try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
    toast.info(WEB_CALL_DISABLED_MESSAGE);
  };

  const declineResidentCall = async (call) => {
    if (!call?.id) return;
    try {
      try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
      await api.post(`/society/calls/${call.id}/end`, { status: "missed" });
      toast.success("Call declined");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not decline call"));
    }
  };

  const copyApproval = async (url) => {
    try {
      await navigator.clipboard?.writeText(url);
      toast.success("Approval link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const startCall = async () => {
    toast.info(WEB_CALL_DISABLED_MESSAGE);
  };

  const startDirectOwnerCall = async () => {
    if (!selectedOwner?.id) {
      toast.error("Select owner flat first");
      return;
    }
    toast.info(WEB_CALL_DISABLED_MESSAGE);
  };

  const checkoutVisitor = async (visitor) => {
    try {
      await api.patch(`/society/visitors/${visitor.id}/checkout`);
      toast.success("Visitor checked out");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not checkout visitor"));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const openCallRingtoneSettings = () => {
    try {
      window.RpSocietyAndroid?.openCallRingtoneSettings?.();
      toast.info("Open Incoming RP Society Calls and choose Sound / Ringtone.");
    } catch {
      toast.info("Open Android app notification settings and choose Incoming RP Society Calls sound.");
    }
  };

  // Same fix as the resident app: Android 14+ full-screen call UI and battery
  // optimization both have to be granted manually per device, and guards miss
  // resident calls the same way residents miss guard calls without this.
  const fixBackgroundCallAlerts = () => {
    try {
      window.RpSocietyAndroid?.requestIgnoreBatteryOptimizations?.();
      window.RpSocietyAndroid?.openFullScreenIntentSettings?.();
      toast.info("Allow the battery prompt, then enable Full screen notifications for RP Society.");
    } catch {
      toast.info("Open Android Settings > Apps > RP Society and allow full-screen notifications plus unrestricted battery usage.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-28 text-slate-950">
      <IncomingCallScreen
        call={isNativeApp ? null : leadRingingCall}
        onAnswer={() => answerResidentCall(leadRingingCall)}
        onDecline={() => declineResidentCall(leadRingingCall)}
      />
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-950 text-white">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">RP Society Guard</div>
              <h1 className="text-lg font-black leading-tight">{user?.client_name || "Visitor Desk"}</h1>
              <div className="text-xs font-semibold text-slate-500">{user?.name || "Guard"} | {form.gateName || "Main Gate"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="icon" onClick={openCallRingtoneSettings} aria-label="Call ringtone">
              <Bell className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={fixBackgroundCallAlerts} aria-label="Fix missed calls when phone is locked" className="border-amber-300 bg-amber-50 text-amber-900">
              <Bell className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={load} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={doLogout} aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4">
        <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button onClick={() => setActiveView("entry")} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-sm">
            <Plus className="h-5 w-5 shrink-0 text-blue-700" />
            <div className="min-w-0">
              <div className="truncate text-sm font-black">New Entry</div>
              <div className="truncate text-xs text-slate-500">Add visitor</div>
            </div>
          </button>
          <button onClick={() => setActiveView("pending")} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-sm">
            <PhoneCall className="h-5 w-5 shrink-0 text-amber-700" />
            <div className="min-w-0">
              <div className="truncate text-sm font-black">{visitors.filter((item) => item.status === "pending").length} Pending</div>
              <div className="truncate text-xs text-slate-500">Call owner</div>
            </div>
          </button>
          <button onClick={load} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-sm">
            <RefreshCw className="h-5 w-5 shrink-0 text-emerald-700" />
            <div className="min-w-0">
              <div className="truncate text-sm font-black">Refresh</div>
              <div className="truncate text-xs text-slate-500">Sync now</div>
            </div>
          </button>
          <button onClick={openCallRingtoneSettings} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-sm">
            <Bell className="h-5 w-5 shrink-0 text-slate-700" />
            <div className="min-w-0">
              <div className="truncate text-sm font-black">Ringtone</div>
              <div className="truncate text-xs text-slate-500">Call sound</div>
            </div>
          </button>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">Direct owner call</div>
              <h2 className="text-xl font-black leading-tight text-slate-950">Call flat without visitor entry</h2>
            </div>
            <button
              type="button"
              onClick={startDirectOwnerCall}
              disabled={busy || !selectedOwner}
              aria-label="Call owner now"
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${selectedOwner ? "bg-blue-700 text-white shadow-sm" : "bg-blue-50 text-blue-700"} disabled:opacity-50`}
            >
              <PhoneCall className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2">
            <Select value={form.wing} onValueChange={(value) => setForm((f) => ({ ...f, wing: value }))}>
              <SelectTrigger className="h-14 rounded-xl border-slate-300 bg-slate-50 text-base font-black">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{wings.map((wing) => <SelectItem key={wing} value={wing}>{wing}</SelectItem>)}</SelectContent>
            </Select>
            <Input
              className="h-14 rounded-xl border-slate-300 bg-slate-50 text-xl font-black"
              value={form.flatNumber}
              onChange={(e) => setForm((f) => ({ ...f, flatNumber: e.target.value.toUpperCase() }))}
              placeholder="Room / flat no."
              inputMode="numeric"
            />
          </div>

          <div className={`mt-3 rounded-xl border p-3 ${selectedOwner ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
            {selectedOwner ? (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-base font-black text-slate-950">{selectedOwner.ownerName || "Owner"}</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-600">
                    {selectedOwner.wing} {selectedOwner.flatNumber} | {selectedOwner.ownerMobile || "mobile not saved"}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-xs font-black text-white">FOUND</span>
              </div>
            ) : (
              <div className="text-sm font-semibold text-amber-800">Enter wing and flat number. Owner will appear here before call.</div>
            )}
          </div>
        </section>

        {activeView === "entry" && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              <div>
                <h2 className="text-xl font-black">New visitor</h2>
                <p className="text-sm text-slate-500">Search the flat, enter visitor details, then call the owner for approval.</p>
              </div>
            </div>
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <span className="font-bold text-slate-950">Guard:</span> {user?.name || "Logged in guard"}
              <span className="mx-2 text-slate-300">|</span>
              <span className="font-bold text-slate-950">Gate:</span> {form.gateName || "Main Gate"}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Visitor name"><Input className="h-11" value={form.visitorName} onChange={(e) => setForm((f) => ({ ...f, visitorName: e.target.value }))} placeholder="Enter visitor name" /></Field>
              <Field label="Visitor mobile"><Input className="h-11" value={form.visitorMobile} onChange={(e) => setForm((f) => ({ ...f, visitorMobile: e.target.value }))} inputMode="tel" /></Field>
              <div className="sm:col-span-2">
                <Field label="Search owner / flat / intercom">
                  <Input className="h-11" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} placeholder="Search A 304, owner name, mobile, intercom" />
                </Field>
                {ownerMatches.length > 0 && (
                  <div className="mt-2 grid gap-2">
                    {ownerMatches.map((owner) => (
                      <button key={owner.id} type="button" onClick={() => selectOwner(owner)} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-left text-sm">
                        <span className="font-black text-slate-950">{owner.wing} {owner.flatNumber}</span>
                        <span className="text-slate-600"> · {owner.ownerName || "Owner"} · {owner.ownerMobile || "No mobile"}</span>
                        {owner.intercomNumber ? <span className="text-slate-500"> · Intercom {owner.wing}-{owner.intercomNumber}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Field label="Wing">
                <Select value={form.wing} onValueChange={(value) => setForm((f) => ({ ...f, wing: value }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{wings.map((wing) => <SelectItem key={wing} value={wing}>{wing}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Flat number"><Input className="h-11" value={form.flatNumber} onChange={(e) => setForm((f) => ({ ...f, flatNumber: e.target.value.toUpperCase() }))} placeholder="101" /></Field>
              <Field label="Purpose"><Input className="h-11" value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))} /></Field>
              <Field label="No. of visitors"><Input className="h-11" type="number" min="1" value={form.visitorCount} onChange={(e) => setForm((f) => ({ ...f, visitorCount: e.target.value }))} /></Field>
              <Field label="Vehicle number"><Input className="h-11" value={form.vehicleNumber} onChange={(e) => setForm((f) => ({ ...f, vehicleNumber: e.target.value.toUpperCase() }))} /></Field>
              <Field label="Resident number">
                <Select value={form.contactTarget} onValueChange={(value) => setForm((f) => ({ ...f, contactTarget: value }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preferred">Owner preference</SelectItem>
                    <SelectItem value="primary">Primary mobile</SelectItem>
                    <SelectItem value="secondary">Secondary mobile</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2"><Field label="Remarks"><Textarea value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} placeholder="Optional note" /></Field></div>
            </div>

            {false && activeCalls.length > 0 && (
              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Incoming resident calls</div>
                <div className="mt-2 grid gap-2">
                  {activeCalls.map((call) => (
                    <Button key={call.id} type="button" className="justify-start bg-blue-700 text-white hover:bg-blue-800" onClick={() => answerResidentCall(call)}>
                      <PhoneCall className="h-4 w-4" /> Answer {call.ownerName || "Resident"} · {call.wing} {call.flatNumber}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className={`mt-4 rounded-lg border p-3 ${selectedOwner ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Owner lookup</div>
              {selectedOwner ? (
                <div className="mt-1 text-sm">
                  <span className="font-black text-slate-950">{selectedOwner.ownerName}</span>
                  <span className="text-slate-600"> · {selectedOwner.ownerMobile || "mobile not saved"}</span>
                </div>
              ) : (
                <div className="mt-1 text-sm text-amber-800">Enter wing and flat. Owner will be fetched automatically.</div>
              )}
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <Button disabled={busy || !selectedOwner} onClick={() => createVisitor(true)} className="h-12 bg-blue-700 text-base font-black text-white hover:bg-blue-800">
                <PhoneCall className="h-5 w-5" /> Create & Call Owner
              </Button>
              <Button disabled={busy} onClick={() => createVisitor(false)} className="h-12 bg-slate-950 text-base font-black text-white hover:bg-slate-800">
                <Plus className="h-5 w-5" /> Create Entry Only
              </Button>
            </div>
          </section>
        )}

        {activeView !== "entry" && (
          <section className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input className="h-12 bg-white pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search visitor, flat, owner, mobile" />
            </div>
            <div className="grid gap-3">
              {visibleVisitors.map((visitor) => (
                <VisitorCard key={visitor.id} visitor={visitor} onCopy={copyApproval} onCall={startCall} onCheckout={checkoutVisitor} />
              ))}
              {visibleVisitors.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
                  <Clock className="mx-auto h-8 w-8 text-slate-400" />
                  <div className="mt-2 font-black">No visitor entries</div>
                  <div className="mt-1 text-sm text-slate-500">Create a new visitor entry from the Entry tab.</div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white px-3 py-2 shadow-[0_-12px_30px_rgba(15,23,42,0.08)]">
        <div className="mx-auto grid max-w-3xl grid-cols-3 gap-2">
          <button onClick={() => setActiveView("entry")} className={`flex h-14 flex-col items-center justify-center rounded-lg text-xs font-black ${activeView === "entry" ? "bg-slate-950 text-white" : "text-slate-700"}`}>
            <Plus className="h-5 w-5" /> Entry
          </button>
          <button onClick={() => setActiveView("pending")} className={`flex h-14 flex-col items-center justify-center rounded-lg text-xs font-black ${activeView === "pending" ? "bg-slate-950 text-white" : "text-slate-700"}`}>
            <CheckCircle2 className="h-5 w-5" /> Pending
          </button>
          <button onClick={() => setActiveView("history")} className={`flex h-14 flex-col items-center justify-center rounded-lg text-xs font-black ${activeView === "history" ? "bg-slate-950 text-white" : "text-slate-700"}`}>
            <Clock className="h-5 w-5" /> History
          </button>
        </div>
      </nav>
    </div>
  );
}
